import type { BootRunView } from "@/store/bootRun";

/** The looks the rail can show for a boot: blocking while a cold boot bootstraps, a re-bootstrap, or background. */
export type BootIndicatorLook = "blocking" | "rebootstrap" | "background";

/**
 * The look for a run, or `undefined` when the rail shows nothing. The one place a run turns into a look.
 *
 * `ready` shows nothing. A re-bootstrap always has its own look and never blocks. A cold boot blocks while it
 * bootstraps; until the bootstrap has decided the kind, nothing shows, which the fade-in delay covers. Every other
 * stage of work is background.
 */
export function bootIndicatorLookFor(
  run: Pick<BootRunView, "stage" | "kind" | "trigger">,
): BootIndicatorLook | undefined {
  if (run.stage === "ready") return undefined;
  if (run.trigger === "rebootstrap") return "rebootstrap";
  if (run.stage === "bootstrapping") {
    if (run.kind === undefined) return undefined;
    return run.kind === "cold" ? "blocking" : "background";
  }
  return "background";
}

/** The animations the left rail can show for boot work. */
export type BootIndicatorVisual = "shimmer" | "dots";

/** Which animation each look shows. Switching the rail's look is an edit to this map alone. */
export const BOOT_INDICATOR_VISUALS: Record<BootIndicatorLook, BootIndicatorVisual> = {
  blocking: "shimmer",
  background: "dots",
  rebootstrap: "dots",
};

/** Tooltip and accessible status text for each look. */
export const BOOT_INDICATOR_LABELS: Record<BootIndicatorLook, string> = {
  blocking: "Loading your workspace…",
  background: "Syncing in the background…",
  rebootstrap: "Refreshing your workspace…",
};

/** How strongly an animation runs for a look: brightest and fastest while blocking, dimmest in the background. */
export type BootIndicatorIntensity = {
  /** CSS colour of the moving mark. */
  color: string;
  /** Opacity the mark fades in to. */
  opacity: number;
  /** Seconds per loop. */
  durationSeconds: number;
  /** Runs the motion backwards, marking a re-bootstrap as "starting over". */
  reverse: boolean;
  /** Glow radius in pixels; 0 for none. */
  glowPx: number;
};

export const BOOT_INDICATOR_INTENSITY: Record<BootIndicatorLook, BootIndicatorIntensity> = {
  blocking: {
    color: "var(--color-brand-primary-300)",
    opacity: 1,
    durationSeconds: 1.4,
    reverse: false,
    glowPx: 8,
  },
  background: {
    color: "var(--color-brand-primary-400)",
    opacity: 0.42,
    durationSeconds: 3.4,
    reverse: false,
    glowPx: 0,
  },
  rebootstrap: {
    color: "var(--color-brand-primary-200)",
    opacity: 0.75,
    durationSeconds: 2.1,
    reverse: true,
    glowPx: 4,
  },
};

/** Delay before a mark fades in, so work that finishes quickly shows nothing. */
export const BOOT_INDICATOR_FADE_IN = "opacity 400ms ease 300ms";

/** How a mark fades out once its look ends. */
export const BOOT_INDICATOR_FADE_OUT = "opacity 600ms ease";
